import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Church, Factory, HandHeart, Landmark, Users } from "lucide-react";
import { useGuidedTour } from "@/lib/guidedTour";
import { useNarrationCue } from "@/components/clientDemo/tour/TourCuePointer";

const ORGS = [
  { phrase: "government agencies", label: "Government", Icon: Landmark },
  { phrase: "faith organizations", label: "Faith Organizations", Icon: Church },
  { phrase: "employers", label: "Employers & Business", Icon: Factory },
  { phrase: "service providers", label: "Service Providers", Icon: HandHeart },
  { phrase: "residents", label: "Residents & Families", Icon: Users },
];
const PHRASES = ORGS.map((o) => o.phrase);
const HOLD_MS = 2000;

// Steps through the orgs once narration reaches the first one, holding each long enough to read.
function useOrgSequence(active) {
  const cue = useNarrationCue(PHRASES, active);
  const [idx, setIdx] = useState(-1);
  const started = cue >= 0;
  useEffect(() => {
    if (!active || !started) { setIdx(-1); return undefined; }
    setIdx(0);
    const ids = ORGS.slice(1).map((_, i) => setTimeout(() => setIdx(i + 1), HOLD_MS * (i + 1)));
    return () => ids.forEach(clearTimeout);
  }, [active, started]);
  return idx;
}

// Lives above the tour dimmer, in the open space left of the Beat 01 hero.
function useStageSpot(active) {
  const [spot, setSpot] = useState(null);
  useEffect(() => {
    if (!active) { setSpot(null); return undefined; }
    const measure = () => {
      const c = document.querySelector('[data-testid="exchange-canvas"]')?.getBoundingClientRect();
      const h = document.querySelector('[data-testid="exchange-beat01-hero"]')?.getBoundingClientRect();
      if (!c || !h || h.left - c.left < 180) return setSpot(null);
      setSpot({ x: c.left + (h.left - c.left) / 2, y: h.top + h.height / 2 });
    };
    measure();
    const id = setInterval(measure, 200);
    return () => clearInterval(id);
  }, [active]);
  return spot;
}

export default function ExchangeOrgVisuals() {
  const { isActive, current } = useGuidedTour();
  const active = isActive && current?.id === "beat01";
  const idx = useOrgSequence(active);
  const spot = useStageSpot(active);
  const org = ORGS[idx];
  if (!spot) return null;
  return (
    <div aria-hidden className="fixed z-[93] pointer-events-none" style={{ left: spot.x, top: spot.y }}
      data-testid="exchange-org-visuals" data-org-index={idx}>
      <AnimatePresence>
        {org && (
          <motion.div
            key={org.label}
            data-testid={`exchange-org-visual-${idx}`}
            className="absolute flex flex-col items-center gap-3 w-56"
            style={{ x: "-50%", y: "-50%" }}
            initial={{ opacity: 0, scale: 0.75, filter: "blur(8px)" }}
            animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
            exit={{ opacity: 0, scale: 1.12, filter: "blur(10px)" }}
            transition={{ duration: 0.8, ease: [0.2, 0.8, 0.2, 1] }}
          >
            <div className="relative">
              <div className="absolute -inset-8 rounded-full blur-2xl bg-[radial-gradient(circle,rgba(212,175,55,0.4),transparent_70%)]" />
              <org.Icon className="relative w-28 h-28 lg:w-32 lg:h-32 text-[#F4D88A]" strokeWidth={1} style={{ filter: "drop-shadow(0 0 18px rgba(212,175,55,0.7))" }} />
            </div>
            <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-[#F4D88A] text-center">{org.label}</span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
